import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS, API_VERSION } from './config';
import { toast } from 'react-toastify';
import { showApiMessage } from '../modals/global/ApiMessageModal/ApiMessageModal';

const apiClient = axios.create({
  baseURL: `${API_CONFIG.BASE_URL}${API_VERSION}`,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.headers
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    config.headers['X-Platform'] = 'web';
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const uid = localStorage.getItem(STORAGE_KEYS.UID);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Only add uid header for authenticated requests that need event context
    if (uid && !config.url.includes('/auth/') && !config.url.includes('/events/register')) {
      config.headers.uid = uid;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // If backend returns HTTP 200 but payload has success: false or an error status (e.g. 400, 422)
    if (response.data && typeof response.data === 'object') {
      const res = response.data;
      if (res.success === false || (res.status && res.status !== 800 && res.status !== 200 && res.status !== 201)) {
        showApiMessage(res);
      }
    }
    return response.data;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const isLoginEndpoint = error.config?.url?.includes('/auth/login');

      // Dispatch exact message from API response data
      const apiMessage = data?.message || (status === 422 ? 'Validation error' : 'An error occurred');

      switch (status) {
        case 401:
          // Force logout for all endpoints if unauthorized
          if (!isLoginEndpoint) {
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER);
            localStorage.removeItem(STORAGE_KEYS.UID);
            localStorage.removeItem(STORAGE_KEYS.USER_ID);
            localStorage.removeItem(STORAGE_KEYS.EVENT_ID);
            showApiMessage({ success: false, status: 401, message: 'Session expired. Please login again.' });
            window.location.href = '/login';
          }
          break;
        case 403:
          showApiMessage({ success: false, status: 403, message: apiMessage || 'You do not have permission to perform this action' });
          break;
        case 404:
          showApiMessage({ success: false, status: 404, message: apiMessage || 'Resource not found' });
          break;
        case 422:
          showApiMessage({ success: false, status: 422, message: apiMessage });
          break;
        case 500:
          showApiMessage({ success: false, status: 500, message: apiMessage || 'Server error. Please try again later.' });
          break;
        default:
          showApiMessage({ success: false, status: status || 400, message: apiMessage });
      }
    } else if (error.request) {
      showApiMessage({ success: false, status: 0, message: 'Network error. Please check your connection.' });
    } else {
      showApiMessage({ success: false, status: 0, message: error.message || 'An unexpected error occurred' });
    }

    // Normalize rejection to an Error instance so callers receive a readable message
    const normalizedMessage = error.response?.data?.message || error.message || 'An error occurred';
    const normalizedError = new Error(normalizedMessage);
    normalizedError.status = error.response?.status;
    normalizedError.data = error.response?.data;
    normalizedError.original = error;

    return Promise.reject(normalizedError);
  }
);

export default apiClient;